import type { TaxProfile } from "@investhinks/api-client";
import React from "react";
import { Alert, Card, CardBody, Col, Row } from "reactstrap";

import { TAX_REFERRER_TYPES } from "../taxProfileConstants";

type ConfirmStepTaxProfile = Pick<TaxProfile, "profileType" | "taxIdType" | "taxIdNumber" | "name" | "fiscalAddress" | "irpfRate" | "altaDate"> & Record<string, unknown>;

interface ConfirmStepProps {
  taxProfile: ConfirmStepTaxProfile | null;
  t: (key: string, fallback?: string) => string;
}

const ConfirmStep = ({ taxProfile, t }: ConfirmStepProps) => {
  const getTypeSummary = () => {
    const type = taxProfile?.profileType;
    if (!type) return {};

    const info = TAX_REFERRER_TYPES.find((item) => item.value === type);
    return {
      type: t(`referrerTypes.${type}`, String(type).replace(/_/g, " ")),
      isBusiness: info?.isBusiness,
    };
  };

  const summary = getTypeSummary();

  return (
    <div>
      <h5 className="mb-3">{t("steps.confirm.title", "Confirm Your Details")}</h5>
      <p className="text-muted mb-4">
        {t("steps.confirm.description", "Please review your information before completing the setup.")}
      </p>

      <Card>
        <CardBody>
          <Row>
            <Col md={6}>
              <p>
                <strong>{t("steps.confirm.type", "Type")}:</strong>
                <br />
                {summary.type}
              </p>
            </Col>
            <Col md={6}>
              <p>
                <strong>{t("steps.confirm.taxId", "Tax ID")}:</strong>
                <br />
                {taxProfile?.taxIdType} {taxProfile?.taxIdNumber?.toUpperCase()}
              </p>
            </Col>
          </Row>

          {taxProfile?.name && (
            <p>
              <strong>{t("steps.confirm.company", "Company")}:</strong>
              <br />
              {taxProfile.name}
            </p>
          )}

          {taxProfile?.fiscalAddress && (
            <p>
              <strong>{t("steps.confirm.address", "Address")}:</strong>
              <br />
              {taxProfile.fiscalAddress}
            </p>
          )}

          {taxProfile?.irpfRate != null && (
            <p>
              <strong>{t("steps.confirm.irpf", "IRPF Rate")}:</strong>
              <br />
              {taxProfile.irpfRate === 0.07 && taxProfile.altaDate ? (
                <>
                  {t("steps.confirm.reducedRateUntil", "7% until")}{" "}
                  {new Date(
                    new Date(taxProfile.altaDate).setFullYear(new Date(taxProfile.altaDate).getFullYear() + 3)
                  ).toLocaleDateString()}
                  {", "}
                  {t("steps.confirm.thenStandardRate", "then 15%")}
                </>
              ) : (
                <>{Math.round(taxProfile.irpfRate * 100)}%</>
              )}
            </p>
          )}
        </CardBody>
      </Card>

      <Alert color="info" className="mt-3">
        <i className="fas fa-info-circle mr-2" />
        {t("steps.confirm.note", "You can update these details later from your profile settings.")}
      </Alert>
    </div>
  );
};

export default ConfirmStep;
